export const navLinks = [
  { label: 'The Collection', href: '#collection' },
  { label: 'Bonus Kits', href: '#bonuses' },
  { label: 'FAQs', href: '#faq' },
]

export const hero = {
  eyebrow: 'For riding instructors & teaching teams',
  title: 'Give Every Riding Lesson a Clear Plan.',
  subheading:
    'Bring structure to your teaching with progressive lesson resources, practical activities and assessment tools for children, teenagers and adults.',
  productLine: 'Horse Riding Instructor’s Complete Teaching Toolkit',
  imageAlt:
    'Horse Riding Instructor’s Complete Teaching Toolkit: eight teaching volumes, five bonus kits and lesson-plan screens shown on a tablet and desktop',
  microcopy: '8 teaching volumes • 5 bonus kits • Digital PDF collection',
  secondaryLink: 'See what’s inside',
}

export const facts = [
  '8 Teaching Volumes',
  '5 Bonus Kits',
  'Children, Teens & Adults',
  'Beginner to Advanced',
]

export const problemsSection = {
  title: 'Does lesson planning take more energy than it should?',
  intro:
    'You know your riders. Turning their different needs into a clear teaching plan can still take time.',
  closing:
    'Build your next lesson around an objective, an activity and something you can observe.',
}

export const problems = [
  'You start each lesson plan from a blank page.',
  'Your group includes different levels of confidence and independence.',
  'You need a useful next step, not another random exercise.',
  'Changes in horses, weather or attendance disrupt your plan.',
  'Progress is difficult to explain without a clear record.',
  'Planning, teaching and follow-up live in different places.',
]

export const collectionIntro = {
  eyebrow: 'One organised teaching library',
  title: 'A practical framework for the lessons ahead.',
  body: 'Move from teaching foundations through beginner, intermediate and advanced work, with dedicated resources for polework, horse care, group activities and assessment.',
  note: 'Choose by demonstrated readiness and teaching context, not age alone.',
}

export const collectionBenefits = [
  {
    title: 'Plan with purpose',
    text: 'Choose a teaching objective and find resources to support it.',
  },
  {
    title: 'Adapt to the partnership',
    text: 'Consider the rider’s demonstrated ability, confidence and the horse’s suitability.',
  },
  {
    title: 'Make progress visible',
    text: 'Record observations and use them to shape the next lesson.',
  },
]

export const volumesSection = {
  title: '8 volumes. A connected teaching progression.',
  footnote: 'Designed for instructors teaching across ages and stages.',
}

export const volumes = [
  {
    number: '01',
    title: 'Teaching Foundations & Rider Assessment',
    benefit: 'Start with a clearer picture of the rider and the lesson.',
    bullets: ['Teaching foundations', 'Rider assessment', 'Goals and suitable starting points'],
  },
  {
    number: '02',
    title: 'Beginner Riding — Progressive Lesson Plans',
    benefit: 'Give early learning a structured starting point.',
    bullets: [
      'Progressive beginner lessons',
      'Clear teaching objectives',
      'Observation and adaptation',
    ],
  },
  {
    number: '03',
    title: 'Intermediate Riding — Progressive Lesson Plans',
    benefit: 'Connect developing skills with purposeful practice.',
    bullets: [
      'Intermediate lesson progression',
      'Consolidation and independence',
      'Practical assessment points',
    ],
  },
  {
    number: '04',
    title: 'Advanced Riding & Flatwork',
    benefit: 'Bring precision and thoughtful progression to advanced teaching.',
    bullets: ['Advanced flatwork planning', 'Quality of execution', 'Evidence-led next steps'],
  },
  {
    number: '05',
    title: 'Polework & Jumping Progressions',
    benefit: 'Plan progressive sessions around the horse–rider partnership.',
    bullets: [
      'Polework teaching resources',
      'Jumping progression planning',
      'Prerequisites and adaptation',
    ],
  },
  {
    number: '06',
    title: 'Groundwork & Horse Care Lessons',
    benefit: 'Give unmounted learning a clear place in your programme.',
    bullets: [
      'Groundwork lesson resources',
      'Horse-care workshops',
      'Practical observation and discussion',
    ],
  },
  {
    number: '07',
    title: 'Teaching Activities & Group Lessons',
    benefit: 'Keep group teaching purposeful and organised.',
    bullets: ['Teaching activity ideas', 'Group lesson organisation', 'Different readiness levels'],
  },
  {
    number: '08',
    title: 'Instructor’s Planning & Assessment Resources',
    benefit: 'Keep your planning and progress records connected.',
    bullets: [
      'Reusable planning forms',
      'Assessment and progress records',
      'Internal recognition resources',
    ],
  },
]

export const audienceLabels = [
  'Children',
  'Teenagers',
  'Adults',
  'Beginners',
  'Intermediate',
  'Advanced',
]

export const bonusesSection = {
  eyebrow: 'Included with the collection',
  title: '5 bonus kits for the moments around the lesson.',
  intro:
    'Support the conversations, changes and events that sit alongside your regular teaching.',
  closingValue: 'US$94',
}

export const bonuses = [
  {
    number: '01',
    icon: 'rescue',
    title: 'The Last-Minute Riding Lesson Rescue Kit',
    image:
      'https://cdn.magicpatterns.com/uploads/gD5r8uneKC3nNwCepPU1Tr/2b289d8d-9e6a-4bf2-816b-90787a0077ce-mockup.webp',
    benefit:
      'Find a purposeful alternative when conditions, attendance or the original plan change.',
    value: 'US$15',
  },
  {
    number: '02',
    icon: 'confidence',
    title: 'The Rider Confidence & Comeback Coaching Kit',
    image:
      'https://cdn.magicpatterns.com/uploads/aVryN5c9bXcKieDvcpWeGs/1076b757-8056-411c-9fac-6520ff356cd7-mockup.webp',
    benefit:
      'Structure supportive conversations and suitable next steps for hesitant or returning riders.',
    value: 'US$20',
  },
  {
    number: '03',
    icon: 'show',
    title: 'The First Show & Competition Readiness Kit',
    image:
      'https://cdn.magicpatterns.com/uploads/16iHzPxY3ATK8uSDbaESS7/e240ac95-9f00-49ac-b51a-a33c9ccc62f4-mockup.webp',
    benefit:
      'Organise preparation, event-specific checks and reflection for a suitable first competition.',
    value: 'US$27',
  },
  {
    number: '04',
    icon: 'communication',
    title: 'The Riding School Communication Kit',
    image:
      'https://cdn.magicpatterns.com/uploads/o6Kib7cUTk84yfKHmNBA8Z/7196afbb-5ce2-4cce-abb4-be3d51f367cf-mockup.webp',
    benefit:
      'Adapt ready-written messages for learners, guardians and everyday riding-school communication.',
    value: 'US$17',
  },
  {
    number: '05',
    icon: 'camp',
    title: 'The Riding Camp & Clinic Planning Kit',
    image:
      'https://cdn.magicpatterns.com/uploads/eqrYHWV2VzcNFeQurXQMtX/b8765f74-7d72-4b8c-accb-35bd7af8aeec-mockup.webp',
    benefit:
      'Plan learning objectives, schedules, responsibilities and activities for camps and clinics.',
    value: 'US$15',
  },
]

export const processSection = {
  title: 'From preparation to the next lesson.',
}

export const processSteps = [
  {
    number: '1',
    title: 'Choose the focus',
    text: 'Start with the rider’s needs and a clear learning objective.',
  },
  {
    number: '2',
    title: 'Select the resources',
    text: 'Open the relevant volume and choose the lesson, activity or planning tool.',
  },
  {
    number: '3',
    title: 'Adapt and teach',
    text: 'Adjust to the rider, horse, environment and available support.',
  },
  {
    number: '4',
    title: 'Observe and review',
    text: 'Record what happened and plan the next suitable step.',
  },
]

export const offerSection = {
  title: 'Your complete teaching collection. One clear price.',
  includesLine: 'Includes all 8 volumes + 5 bonus kits',
  microcopy: 'Digital resources for instructors. No physical books or equipment are shipped.',
}

export const offerChecklist = [
  'All 8 teaching volumes',
  'All 5 bonus kits',
  'Resources for children, teenagers and adults',
  'Beginner through advanced teaching',
  'Lesson planning and assessment tools',
  'Digital PDF format',
]

export const useCasesSection = {
  title: 'Built around real teaching situations.',
}

export const useCases = [
  {
    title: 'A new rider joins',
    text: 'Begin with assessment and select a suitable starting point.',
  },
  {
    title: 'A familiar task needs more purpose',
    text: 'Choose an objective, adapt the activity and define what to observe.',
  },
  {
    title: 'Your programme needs consistency',
    text: 'Connect lesson plans, communication and progress records.',
  },
]

export const guaranteeSection = {
  title: '7-Day Money-Back Guarantee',
  body: 'Get the complete toolkit with a 7-day money-back guarantee.',
  supporting: '8 teaching volumes. 5 bonus kits. One organised collection.',
}

export const faqs = [
  {
    question: 'Who is this toolkit for?',
    answer:
      'Riding instructors and riding-school teaching teams working with children, teenagers and adults.',
  },
  {
    question: 'Is it only for children or beginners?',
    answer:
      'No. The collection covers teaching across ages and includes beginner, intermediate and advanced resources. Select activities according to demonstrated readiness and context.',
  },
  {
    question: 'What is included?',
    answer:
      'Eight core teaching volumes and five bonus kits covering lesson changes, rider confidence, first-show preparation, communication, and camps or clinics.',
  },
  {
    question: 'Are the five bonus kits included?',
    answer: 'Yes. All five form part of the complete collection.',
  },
  {
    question: 'Is this a physical product?',
    answer:
      'No. This is a digital PDF collection. The books and devices in the mockup illustrate the resources; physical items are not included.',
  },
  {
    question: 'Can I use it for group lessons?',
    answer:
      'Yes. Volume 7 focuses on teaching activities and group lessons. Adapt plans to the participants, horses, facilities and supervision available.',
  },
  {
    question: 'Does it replace instructor training?',
    answer:
      'No. It supports professional lesson planning and teaching decisions. It is not an accredited qualification or a substitute for appropriate training and supervision.',
  },
  {
    question: 'Do riders need to follow every volume in order?',
    answer:
      'No. Use the progression as a guide and select resources according to the rider’s demonstrated skills and the teaching situation.',
  },
  {
    question: 'Is there a guarantee?',
    answer: 'Yes. The complete toolkit comes with a 7-day money-back guarantee.',
    linksRefundPolicy: true,
  },
]

export const finalCta = {
  title: 'Bring a clearer plan to your next lesson.',
  body: 'Connect your teaching objectives, activities and progress records with one organised resource collection.',
  microcopy: '8 teaching volumes • 5 bonus kits • All ages and levels',
}

export const footer = {
  tagline: 'Independent digital teaching resources for riding instructors.',
}
